// Import Dependencies
import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

// Local Imports
import { useIsomorphicEffect } from 'hooks';
import { defaultTheme } from 'configs/theme.config';
import i18n from 'i18n/config';
import { LocaleContext } from './context';
import { locales } from 'i18n/langs';

// ----------------------------------------------------------------------

// Set the initial language from i18n or fallback to the default theme language
const initialLang =
  localStorage.getItem('i18nextLng') || defaultTheme.defaultLang;

const initialDir = i18n.dir(initialLang);

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState(initialLang);
  const [direction, setDirection] = useState(initialDir);
  const [isReady, setIsReady] = useState(false);

  // Function to update the locale dynamically
  const updateLocale = useCallback(async (newLocale) => {
    try {
      // Dynamically load the locale and update dependencies
      await locales[newLocale].dayjs();
      dayjs.locale(newLocale);
      const i18nResources = await locales[newLocale].i18n();

      // Add or replace the resource bundle
      if (i18n.hasResourceBundle(newLocale, 'translations')) {
        i18n.removeResourceBundle(newLocale, 'translations');
      }
      i18n.addResourceBundle(
        newLocale,
        'translations',
        i18nResources,
        true,
        true
      );
      i18n.changeLanguage(newLocale);

      // Update the state if the locale changes
      setLocale(newLocale);
    } catch (error) {
      console.error('Failed to update locale:', error);

      // Fallback to updating language in case of an error
      i18n.changeLanguage(newLocale);
      setLocale(newLocale);
    }
  }, []);

  // Load the initial locale resources and preload both languages
  useIsomorphicEffect(() => {
    const loadTranslations = async () => {
      // Preload both English and French translations
      const languages = ['en', 'fr'];
      for (const lang of languages) {
        try {
          if (locales[lang]?.i18n) {
            const resources = await locales[lang].i18n();
            if (i18n.hasResourceBundle(lang, 'translations')) {
              i18n.removeResourceBundle(lang, 'translations');
            }
            i18n.addResourceBundle(lang, 'translations', resources, true, true);
          }
        } catch (error) {
          console.error(`Failed to load ${lang} translations:`, error);
        }
      }

      // Set the current locale
      if (locale) {
        await updateLocale(locale);
      }

      // Mark translations as ready
      setIsReady(true);
    };

    loadTranslations();
  }, []);

  // Update text direction based on the current locale
  useIsomorphicEffect(() => {
    const newDir = i18n.dir(locale);
    if (newDir !== direction) {
      setDirection(newDir);
    }
  }, [locale]);

  useIsomorphicEffect(() => {
    document.documentElement.dir = direction;
  }, [direction]);

  // Show nothing until translations are loaded to prevent flash of translation keys
  if (!isReady) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-white dark:bg-dark-900">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    );
  }

  return (
    <LocaleContext
      value={{
        locale,
        updateLocale,
        direction,
        setDirection,
        isRtl: direction === 'rtl',
      }}
    >
      {children}
    </LocaleContext>
  );
}

LocaleProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
